// The System overview (`/admin/system`): every box, database and Redis the metrics hub watches, and what needs
// attention. Built from the NextAdmin demo `/saas` dashboard: headline numbers, then a 12-column grid of cards.
import { useQuery } from '@tanstack/react-query';
import { Activity, AlertTriangle, Database, Gauge, Server } from 'lucide-react';
import { DashboardGrid, DashboardPage } from '../../../components/page/DashboardPage.js';
import { Button, Callout, Skeleton } from '../../../components/ui/index.js';
import type { StatStripCell } from '../../../components/ui/index.js';
import { getSystemOverviewOptions } from '../../../generated/api/@tanstack/react-query.gen.js';
import type { SystemOverview } from '../../../generated/api/types.gen.js';
import { PAGE_HELP } from '../../../help/index.js';
import { ControlPlaneCard } from './ControlPlaneCard.js';
import { DataFreshnessCard } from './DataFreshnessCard.js';
import { DatabaseCard } from './DatabaseCard.js';
import { EngineJobsCard } from './EngineJobsCard.js';
import { FindingsCard } from './FindingsCard.js';
import { HostCard } from './HostCard.js';
import { RedisCard } from './RedisCard.js';
import { RedisMetricChart } from './RedisMetricChart.js';

function headline(overview: SystemOverview): StatStripCell[] {
  const online = overview.hosts.filter((host) => host.online).length;
  const urgent = overview.findings.filter((finding) => finding.severity === 'danger').length;
  return [
    {
      label: 'Boxes online',
      value: `${online} of ${overview.hosts.length}`,
      severity: online < overview.hosts.length ? 'danger' : undefined,
      icon: Server,
    },
    { label: 'Databases', value: String(overview.databases.length), icon: Database },
    {
      label: 'Needs attention',
      value: String(overview.findings.length),
      severity: urgent > 0 ? 'danger' : overview.findings.length > 0 ? 'warning' : undefined,
      icon: AlertTriangle,
    },
    {
      label: 'Metrics hub',
      value: overview.store.reachable ? 'Connected' : overview.store.configured ? 'Not answering' : 'Not set up',
      severity: overview.store.reachable ? undefined : 'danger',
      icon: Gauge,
    },
  ];
}

function StoreAlert({ overview, onRetry }: { overview: SystemOverview; onRetry: () => void }) {
  if (overview.store.reachable) return null;
  return (
    <Callout
      tone={overview.store.configured ? 'danger' : 'warning'}
      icon={Activity}
      title={overview.store.configured ? 'The metrics hub is not answering' : 'The metrics hub is not set up'}
      actions={
        <Button size="sm" variant="secondary" onClick={onRetry}>
          Retry
        </Button>
      }
    >
      {overview.store.configured
        ? `The server could not read the hub (${overview.store.message ?? 'no answer'}). The boxes keep their data queued and the charts fill in once it answers.`
        : 'Set SYSTEM_METRICS_PROMETHEUS_URL and SYSTEM_METRICS_GATEWAY_URL on the server (deploy/monitoring/RUNBOOK.md). This server’s own health is below.'}
    </Callout>
  );
}

export function SystemPage() {
  const query = useQuery({ ...getSystemOverviewOptions(), refetchInterval: 30_000 });
  const overview = query.data;
  const nowMs = overview ? new Date(overview.generatedAt).getTime() : 0;

  return (
    <DashboardPage
      title="System"
      subtitle="Every box, database and Redis the platform runs on, this environment's engine jobs and how fresh its data is: how hard they work now and over time. Refreshes every 30 seconds."
      helpContent={PAGE_HELP['system-info']}
      stats={overview ? headline(overview) : undefined}
      alert={
        query.isError ? (
          <Callout tone="danger" icon={AlertTriangle} title="The System overview could not be loaded">
            The server did not answer. Retry in a moment.
          </Callout>
        ) : overview ? (
          <StoreAlert overview={overview} onRetry={() => query.refetch()} />
        ) : undefined
      }
    >
      <DashboardGrid>
        {query.isPending && <Skeleton height={220} className="xl:col-span-12" />}
        {/* The engine's and the data's findings come without the metrics hub: show them whenever there are any. */}
        {overview && (overview.store.reachable || overview.findings.length > 0) && (
          <FindingsCard findings={overview.findings} />
        )}
        {overview?.hosts.map((host) => (
          <HostCard key={host.id} host={host} nowMs={nowMs} />
        ))}
        {overview?.databases.map((database) => (
          <DatabaseCard key={database.id} database={database} />
        ))}
        <RedisCard />
        {overview?.store.reachable && (
          <>
            <div className="xl:col-span-6">
              <RedisMetricChart panel="memory" range="24h" title="Redis memory, last 24 hours" />
            </div>
            <div className="xl:col-span-6">
              <RedisMetricChart panel="commands" range="24h" title="Redis commands, last 24 hours" />
            </div>
          </>
        )}
        <ControlPlaneCard />
        {overview && <EngineJobsCard jobs={overview.engineJobs} nowMs={nowMs} />}
        {overview && <DataFreshnessCard rows={overview.dataFreshness} nowMs={nowMs} />}
      </DashboardGrid>
    </DashboardPage>
  );
}
